import React, { useEffect, useRef, useState } from 'react';
import './SatisfactionVehicule.css';

// Éléments partagés par la liste des réponses et la fiche réponse.

// Sources dont la date de référence est un passage à l'atelier (facture), et
// non une vente : libellés « passage le », étiquette APV.
const SOURCES_PASSAGE = ['crm_passages_atelier', 'a97_sellens_passages_atelier', 'a97_datacar_passages_atelier'];
export const estPassage = (source) => SOURCES_PASSAGE.includes(source);

// Étiquette courte du formulaire : « VN » ou « APV » d'après sa source, au
// lieu de son nom complet (« Satisfaction VN SELLENS » sur trois lignes).
export function etiquetteFormulaire(source, nom) {
  if (source === 'a97_sellens_ventes_vn') return 'VN';
  if (estPassage(source)) return 'APV';
  return nom || '—';
}

// Pastille de note : la couleur dit tout de suite promoteur (9-10) ou
// détracteur (0-8, seuil SELLENS). Sans réponse, un cadre vide ; envoi en échec, un « ! ».
export function NotePastille({ nps, statut, grande = false }) {
  const cls = `sat-note-pastille${grande ? ' sat-note-pastille-grande' : ''}`;
  if (statut === 'echec') return <span className={`${cls} sat-np-echec`} title="Envoi jamais parti">!</span>;
  if (nps === null || nps === undefined) {
    return <span className={`${cls} sat-np-vide`} title={statut === 'repondu' ? 'Pas de note' : 'Pas encore répondu'}>—</span>;
  }
  const n = Number(nps);
  const ton = n >= 9 ? 'sat-np-pro' : 'sat-np-det';
  return <span className={`${cls} ${ton}`}>{n}</span>;
}

// Véhicule de l'envoi (2026-11-03, demande terrain SELLENS) : la plaque,
// comme dans Custeed, pour retrouver la fiche dans DataCar. Le VIN la
// remplace quand elle manque (VN vendu avant immatriculation). Un clic copie
// la valeur exacte à coller dans la recherche DataCar ; en liste, le clic ne
// doit pas ouvrir la fiche de la ligne.
const SIV_COLLE = /^([A-Z]{2})(\d{3})([A-Z]{2})$/;
export function formatPlaque(immat) {
  const p = String(immat || '').trim().toUpperCase();
  return p.replace(SIV_COLLE, '$1-$2-$3');
}

function copierParSelection(texte) {
  const zone = document.createElement('textarea');
  zone.value = texte;
  zone.setAttribute('readonly', '');
  zone.style.position = 'fixed';
  zone.style.opacity = '0';
  document.body.appendChild(zone);
  zone.select();
  try { return document.execCommand('copy'); } finally { document.body.removeChild(zone); }
}

// navigator.clipboard n'existe qu'en contexte sécurisé (pas en HTTP local) et
// peut être refusé par le navigateur : on retombe alors sur la sélection.
function copierTexte(texte) {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(texte).catch(() => {
      if (!copierParSelection(texte)) throw new Error('Copie refusée');
    });
  }
  return copierParSelection(texte) ? Promise.resolve() : Promise.reject(new Error('Copie refusée'));
}

// `statique` : simple affichage, pour une carte déjà cliquable en entier (mobile),
// où un bouton dans le bouton serait invalide.
export function VehiculeEnvoi({ immat, chassis, vinComplet = false, statique = false }) {
  const [copie, setCopie] = useState(false);
  const minuteur = useRef(null);
  useEffect(() => () => clearTimeout(minuteur.current), []);
  const plaque = formatPlaque(immat);
  const vin = String(chassis || '').trim().toUpperCase();
  if (!plaque && !vin) return null;
  const valeur = plaque || vin;
  const copier = (ev) => {
    ev.stopPropagation();
    copierTexte(valeur).then(() => {
      setCopie(true);
      clearTimeout(minuteur.current);
      minuteur.current = setTimeout(() => setCopie(false), 1500);
    }).catch(() => {});
  };
  const libelleVin = vinComplet || vin.length <= 8 ? vin : `…${vin.slice(-8)}`;
  if (statique) {
    return (
      <span className="sat-vehicule">
        {plaque
          ? <span className="plaque-badge sat-plaque">{plaque}</span>
          : <span className="sat-vin"><small>VIN</small>{libelleVin}</span>}
      </span>
    );
  }
  const titre = `${plaque ? 'Plaque' : 'VIN'} ${valeur}${plaque && vin ? ` · VIN ${vin}` : ''} — cliquer pour copier`;
  return (
    <span className="sat-vehicule">
      {plaque ? (
        <button type="button" className="plaque-badge plaque-badge-link sat-plaque" title={titre} onClick={copier}>
          {plaque}
        </button>
      ) : (
        <button type="button" className="sat-vin" title={titre} onClick={copier}>
          <small>VIN</small>{libelleVin}
        </button>
      )}
      {copie && <span className="sat-copie" role="status">Copié</span>}
    </span>
  );
}

// Projets d'achat (2026-11-02) : issue du rappel commercial, et finalités
// de recontact cochées par le client (bloc « Recontact »).
export const ISSUE_PROJET = {
  rdv_pris: { label: 'RDV pris', classe: 'status-success' },
  injoignable: { label: 'Injoignable', classe: 'status-warning' },
  sans_suite: { label: 'Pas de projet finalement', classe: 'status-info' },
};
export const CONTACT_PROJET = [
  { cle: 'prospection_tel', label: '📞 Appel' },
  { cle: 'prospection_sms', label: '📱 SMS' },
  { cle: 'prospection_email', label: '✉️ E-mail' },
];
